import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Segmented } from "../../../components/ui";
import { isoDay } from "../main/dashboard/PeriodFilter";

export const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const FORMATS = ["videos", "shorts", "all"];
const DAY = 24 * 60 * 60 * 1000;

/**
 * Period and format of the rubric pages, kept in the address (`?period=90d`, or `custom` with
 * `start` / `end`; `format=shorts`) so that the overview and a rubric share them. `params` is what the API expects, `null` while the
 * date range is invalid.
 */
export function usePeriod(location, history) {
  const search = new URLSearchParams(location.search);
  const [range, setRangeState] = useState(() => ({ start: search.get("start") || isoDay(Date.now() - 29 * DAY), end: search.get("end") || isoDay(Date.now()) }));
  const period = search.get("period") || "30d";
  const format = FORMATS.includes(search.get("format")) ? search.get("format") : "videos";
  const rangeValid = Boolean(range.start && range.end && range.start <= range.end);
  const write = (next, nextRange = range, nextFormat = format) => {
    const params = new URLSearchParams();
    if (next !== "30d") params.set("period", next);
    if (next === "custom") {
      params.set("start", nextRange.start);
      params.set("end", nextRange.end);
    }
    if (nextFormat !== "videos") params.set("format", nextFormat);
    history.replace({ pathname: location.pathname, search: params.toString() ? `?${params}` : "" });
  };
  const withFormat = (p) => (p && format !== "videos" ? { ...p, format } : p);
  const params = withFormat(period === "custom" ? (rangeValid ? { period, start: range.start, end: range.end } : null) : { period });
  return {
    period,
    format,
    setFormat: (next) => write(period, range, next),
    params,
    range,
    rangeValid,
    search: location.search,
    setPeriod: (next) => write(next),
    setRange: (next) => {
      setRangeState(next);
      if (next.start && next.end && next.start <= next.end) write("custom", next);
    },
  };
}

/** Long videos (default), shorts, or both: they are watched and commented differently. */
export const FormatFilter = ({ state }) => {
  const { t } = useTranslation();
  return (
    <Segmented
      label={t("Format")}
      value={state.format}
      onChange={state.setFormat}
      options={[
        { value: "videos", label: t("Long videos") },
        { value: "shorts", label: t("Shorts") },
        { value: "all", label: t("All") },
      ]}
    />
  );
};

/** Period buttons, and the two dates when « Dates » is picked. */
export const PeriodFilter = ({ state }) => {
  const { t } = useTranslation();
  const today = isoDay(Date.now());
  return (
    <>
      <Segmented
        label={t("Period")}
        value={state.period}
        onChange={state.setPeriod}
        options={[
          { value: "7d", label: t("7 days") },
          { value: "30d", label: t("30 days") },
          { value: "90d", label: t("90 days") },
          { value: "365d", label: t("1 year") },
          { value: "all", label: t("All time") },
          { value: "custom", label: t("Dates") },
        ]}
      />
      {state.period === "custom" && (
        <div className="perf-range">
          <label>
            <span>{t("From")}</span>
            <input type="date" className="ui-input" value={state.range.start} max={state.range.end || today} onChange={(e) => state.setRange({ ...state.range, start: e.target.value })} />
          </label>
          <label>
            <span>{t("To")}</span>
            <input type="date" className="ui-input" value={state.range.end} min={state.range.start} max={today} onChange={(e) => state.setRange({ ...state.range, end: e.target.value })} />
          </label>
          {!state.rangeValid && <span className="perf-range-error" role="alert">{t("The start date must be before the end date")}</span>}
        </div>
      )}
    </>
  );
};

/** Positive / neutral / negative comments as one bar, with the shares. */
export const MoodBar = ({ mood, compact }) => {
  const { t } = useTranslation();
  if (!mood?.total) return <span className="ui-hint">{t("No comment over the period")}</span>;
  const share = (n) => Math.round((n / mood.total) * 100);
  const parts = [
    { key: "positive", value: mood.positive, label: t("positive") },
    { key: "neutral", value: mood.neutral, label: t("neutral") },
    { key: "negative", value: mood.negative, label: t("negative") },
  ];
  return (
    <div className="cat-mood" data-compact={compact || undefined}>
      <div className="cat-mood-bar" role="img" aria-label={parts.map((p) => `${share(p.value)} % ${p.label}`).join(", ")}>
        {parts.map((p) => (p.value ? <span key={p.key} data-tone={p.key} style={{ width: `${share(p.value)}%` }} /> : null))}
      </div>
      <div className="cat-mood-legend">
        {parts.map((p) => (
          <span key={p.key} data-tone={p.key}>
            <i aria-hidden="true"></i>
            {share(p.value)} % {p.label}
          </span>
        ))}
        {!compact && <span className="ui-hint">{t("{{count}} comments", { count: mood.total, formatted: fmt(mood.total) })}</span>}
      </div>
    </div>
  );
};

/** Gap with the platform: « +12 pts », « −30 % », coloured. */
export const Versus = ({ value, reference, unit = "%", label }) => {
  const { t } = useTranslation();
  if (value === null || value === undefined || reference === null || reference === undefined) return null;
  let text;
  let gap;
  if (unit === "pts") {
    gap = Math.round(value - reference);
    text = `${gap > 0 ? "+" : ""}${gap} pts`;
  } else {
    if (!reference) return null;
    gap = Math.round(((value - reference) / reference) * 100);
    text = `${gap > 0 ? "+" : ""}${gap} %`;
  }
  return (
    <small className="perf-delta" data-tone={gap > 0 ? "up" : gap < 0 ? "down" : "flat"}>
      {text} {label || t("vs platform")}
    </small>
  );
};
